import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import NewOffersSwiper from "../components/NewOffersSwiper";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import ChevronLeftIcon from '../images/chevron-left-outlined-rounded.svg';
import ChevronRightIcon from '../images/chevron-right-outlined-rounded.svg';
import TrashXmarkIcon from '../images/trash-xmark.svg';
import thumbsup from '../images/thumbsup.svg';
import styles from "./SearchProperty.module.css";

// Helper functions
const formatWithApostrophe = (value) => {
  if (!value && value !== 0) return '';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/'/g, '')) : value;
  if (isNaN(numValue)) return value;
  const digitsOnly = numValue.toString().replace(/[^\d]/g, '');
  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};



const SponsorCard = styled.div`
  margin-bottom: 20px;          /* Same spacing as propertyCard */
  background-color: #fff;
  border-radius: 4px;           /* Match propertyCard */
  height: 132px;                /* Fixed height for desktop */
  width: 237px;                 /* Fixed width for desktop */
  box-shadow: 0px 1px 3px 1px #00000026; /* Match shadow */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;      /* Center sponsor image */
  box-sizing: border-box;       /* Consistent box model */
  overflow: hidden;             /* Keep image inside */
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }

  /* Hide on tablet & mobile */
  @media (max-width: 1024px) {
    display: none;
  }
  @media (min-width: 2560px) {
    width: 310px;
    height: 170px;
  }
  @media (min-width: 3840px) {
    width: 390px;
    height: 210px;
  }
`;

const SponsorImageContainer = styled.div`
  position: relative;
  height: 0; /* Set height to 0 and use padding for the aspect ratio */
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: #ddd;
  border-radius: 4px;
  overflow: hidden;
  width: 100%; /* Fill card width */
  box-sizing: border-box;
`;

const SponsorImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 105%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;
const DemandDashboardContainer = styled.div`
    display: flex;
    flex-direction: row; /* Default desktop layout */
    width: 100%;
    height: 110vh; /* Fixed height for desktop to enable RightPanel scrolling */
    overflow: hidden; /* Prevent container scroll, only RightPanel scrolls */

    @media (max-width: 992px) {
        flex-direction: column; /* Stack vertically on smaller screens */
        height: auto; /* Allow natural height on mobile */
        overflow-y: auto; /* Allow scrolling for the whole dashboard if content overflows */
    }
`;

const RightPanel = styled.div`
  flex: 1; /* Adjusted flex ratio: RightPanel is bigger (65%) */
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem; /* Responsive gap */
  justify-content: flex-start;
  overflow-y: auto; /* Allow vertical scrolling */
  overflow-x: hidden; /* Prevent horizontal scroll */
  height: 100%; /* Full height of parent container */
  max-height: 110vh; /* Don't exceed viewport height */
  background: #fff;
  box-sizing: border-box;

  @media (max-width: 992px) {
    width: 100%; /* Take full width when stacked */
    min-height: 300px; /* Smaller min-height for stacked layout */
    max-height: none; /* Remove max-height constraint on mobile */
    gap: 1rem;
    height: auto; /* Allow height to adjust when stacked */
    overflow-y: visible; /* Let parent container handle scrolling on mobile */
  }
`;



const TrendingLeftPanel = styled.div`
    border-radius: 4px;
    width: 267px;
    flex-shrink: 0;
    padding: 15px;
    background-color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-right: 1px solid #ddd;
    margin-right: 5px;
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overflow-y: auto;
    box-sizing: border-box;
    align-items: center;

    @media (max-width: 992px) {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #ddd;
        margin-right: 0;
        margin-bottom: 10px;
        max-height: auto;
        overflow-y: hidden;
        height: auto;
        padding-bottom: 30px; 
    }
    @media (max-width: 576px) {
        padding: 10px;
        height: auto;
        padding-bottom: 25px;
    }
    @media (min-width: 2560px) {
        width: 350px;
    }
    @media (min-width: 3840px) {
        width: 450px;
    }
`;

const TrendingHeader = styled.h2`
    font-size: clamp(1.2rem, 3vw, 22px);
    font-weight: 700;
    color: rgba(41, 177, 133, 1);
    margin-bottom: 10px;
    margin-top: 15px;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.28);
    
    @media (max-width: 576px) {
        margin-top: 10px;
        margin-bottom: 5px;
    }
`;
const DotIndicatorContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
`;

const DotIndicator = styled.span`
    height: 10px;
    width: 10px;
    margin: 0 4px;
    background-color: #fff;
    border-radius: 50%;
    display: inline-block;
    cursor: pointer;
    border: 1px solid #ccc;
    
    ${props => props.$active && `
        background-color: #24B386; /* Or your preferred active color */
    `}
`;

const ImageNavButton = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
        background: rgba(215, 215, 215, 0.6);

    border: none;
    cursor: pointer;
    padding: 12px;
    z-index: 10;
    border-radius: 50%;
    transition: background-color 0.3s ease;

    &.left {
        left: 15px;
    }
    
    &.right {
        right: 15px;
    }
    
    &:hover {
        background-color: #C9EBE0;
    }
        `;

const ImageNavIcon = styled.img`
filter: invert(36%) sepia(35%) saturate(1637%) hue-rotate(113deg) brightness(88%) contrast(92%);
    width: 40px;
    height: 40px;
    
`;

const FooterActionsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center; /* Vertically center the items */
    width: 95%; /* Match the width of other elements */
    margin: 25px auto 0 auto;
    
    @media (max-width: 576px) {
        flex-direction: column; /* Stack vertically on small screens */
        align-items: center;
        gap: 15px; /* Add some space between stacked items */
    }
`;

const TrendingCardsWrapper = styled.div`    
  width: 100%; /* This needs to be 100% to contain the slider */
  padding: 0 10px; /* Add some horizontal padding to the slider itself */
  box-sizing: border-box; /* Include padding in width */

  /* Styles for Slick Slider elements */
  .slick-list {
      margin: 0 -5px; /* Counteract padding on slick-slide to maintain overall alignment */
  }
  .slick-slide > div { /* Target the div directly inside slick-slide */
    padding: 0 5px; /* Add internal padding to each card space */
    box-sizing: border-box;
  }
  .slick-prev:before, .slick-next:before {
    font-size: 30px; /* Size of the navigation arrows */
    color: rgba(41, 177, 133, 1); /* Color of the navigation arrows */
  }
  .slick-prev {
    left: -25px; /* Position the previous arrow */
    z-index: 1; /* Ensure arrows are above slides */
  }
  .slick-next {
    right: -25px; /* Position the next arrow */
    z-index: 1;
  }
  .slick-dots li button:before {
    font-size: 10px; /* Size of dots */
    color: #ccc; /* Color of inactive dots */
  }
  .slick-dots li.slick-active button:before {
    color: rgba(41, 177, 133, 1); /* Color of active dot */
  }

  @media (min-width: 993px) { /* On desktop, revert to standard stacking */
    padding: 0; /* Remove horizontal padding on desktop */
    /* Remove any flex/overflow properties that would interfere with vertical stacking */
  }

  @media (max-width: 576px) {
    padding: 0 5px; /* Less padding on very small screens */
    .slick-prev {
      left: -15px;
    }
    .slick-next {
      right: -8px;
    }
  }
`;
const PropertyCard = styled.div`
    margin-bottom: 20px;
    background-color: White;
    border-radius: 8px;
    box-shadow: 0px 1px 3px 1px rgba(0, 0, 0, 0.26);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 237px;
    height: 200px;
    flex-shrink: 0;
    flex-basis: auto;
    flex-grow: 0;
    
    @media (max-width: 768px) {
        width: 100%;
        max-width: 200px;
        min-width: 150px;
        min-height: 180px;
        height: auto;
        flex-basis: calc(50% - 15px);
        flex-grow: 1;
        flex-shrink: 1;
    }
    @media (max-width: 480px) {
        max-width: 80%;
        flex-basis: 100%;
        margin-left: auto;
        margin-right: auto;
    }
    @media (min-width: 2560px) {
        width: 310px;
        height: 260px;
    }
    @media (min-width: 3840px) {
        width: 390px;
        height: 320px;
    }
`;

const PropertyCardSponcer = styled.div`
    margin-bottom: 20px;
    background-color: White;
    border-radius: 8px;
    box-shadow: 0px 1px 3px 1px #00000026;
    height: 133px;
    width: 237px;
    flex-direction: column;
    align-items: center;
    width: 237px;
    flex-shrink: 0;
    flex-basis: auto;
    flex-grow: 0;
    
    @media (max-width: 768px) {
        width: 100%;
        max-width: 200px;
        min-width: 150px;
        min-height: 180px;
        height: auto;
        flex-basis: calc(50% - 15px);
        flex-grow: 1;
        flex-shrink: 1;
    }
    @media (max-width: 480px) {
        max-width: 80%;
        flex-basis: 100%;
        margin-left: auto;
        margin-right: auto;
    }
`;

const PropertyImageContainer = styled.div`
    position: relative;
    background-color: #ddd;
    margin-bottom: 10px;
    border-radius: 4px;
    overflow: hidden;
    width: 100%;
    height: 0;
    padding-top: 56.25%;
    display: flex;
    justify-content: center;
    align-items: center;

    &.large {
    }

    @media (max-width: 576px) {
        margin-bottom: 8px;
    }
`;

const PropertyImage = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
`;
const PropertyImageSponcer = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 105%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
`;

const PropertyText = styled.p`
    font-size: clamp(14px, 2.5vw, 16px);
    color: #555;
    width: calc(100% - 20px);
    height: 46px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-height: 1.3;
    margin: 0 10px 10px 10px;
    box-sizing: border-box;
`;

const PropertyHighlightText = styled.span`
    color: rgba(41, 177, 133, 1);
    font-weight: 700;
    font-size: 20px;
`;
const PropertyCardWrapper = styled.div`
    background-color: #FFFFFF;
    border-radius: 12px;
    padding-top: 5px;
    width: 95%;
    max-width: 800px;
    display: flex;
    flex-direction: column;
    gap: clamp(15px, 3vw, 20px);
    position: relative;
    font-family: 'Inter', sans-serif;
    transform: none;
    margin: 0 auto;

    @media (max-width: 576px) {
        padding: 10px;
        gap: 10px;
        border-radius: 8px;
    }
`;
const MainImageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 0;
    padding-top: 56.25%;
    overflow: hidden;
    background-color: #e0e0e0;
    border-radius: 8px;
`;
const MainImage = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ThumbnailsCarouselWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  padding: 0 10px;

  /* Hide scrollbar for cleaner UI */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// Holds all the thumbnails in a single row
const ThumbnailsContainer = styled.div`
  position: relative; /* now nav buttons will be positioned relative to this */
  display: flex;
  justify-content: center;
  gap: clamp(5px, 1.5vw, 10px);
  flex-wrap: nowrap;
  padding: 5px 0;
  width: fit-content;
  margin: 0 auto;
`;

// 16:9 ratio thumbnail wrapper
const ThumbnailImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 16 / 9; /* ensures perfect 16:9 shape */
  width: clamp(80px, 14vw, 120px); /* wider so 16:9 looks natural */
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid ${props => props.$active ? '#29B185' : '#CCCCCC'};
  transition: border-color 0.3s ease;
  flex-shrink: 0;

  &:hover {
    border-color: #29B185;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; /* keeps image proportion without stretching */
  border-radius: 4px;
  display: block;
`;



const PropertyDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); /* Responsive columns */
  gap: clamp(5px, 2vw, 10px); /* Responsive gap */
  text-align: center;
  align-items: end; /* Align items to the bottom */
  width: 95%; /* Responsive width */
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr)); /* Adjust min width for smaller screens */
  }
  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr)); /* Further adjust for mobile */
  }
`;

const PropertyDetailsGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); /* Responsive columns */
  gap: clamp(5px, 2vw, 10px); /* Responsive gap */
  text-align: center;
  background-color: #C9EBE0;
  border-radius: 50px; /* Pill-shaped */
  height: auto; /* Allow height to adjust based on content */
  width: 95%; /* Responsive width */
  margin: 8px auto 0 auto;
  padding: clamp(8px, 2vw, 12px) 0; /* Responsive padding */
  align-items: center; /* Center items vertically within the pill */

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
  @media (max-width: 576px) {
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
    border-radius: 30px; /* Smaller radius on mobile */
  }
`;
const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem; /* Base font size */
  color: #555;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #29B185;
  margin-bottom: 8px;
  font-size: clamp(0.9rem, 2.5vw, 1.2rem); /* Responsive font size */
`;

const DetailValue = styled.span`
  font-size: clamp(0.8rem, 2.2vw, 1.1rem); /* Responsive font size */
  color: #000;
  white-space: nowrap; /* Prevent values from wrapping */
  overflow: hidden;
  text-overflow: ellipsis; /* Add ellipsis if truncated */
`;
const MatchScoreSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start; /* Align items to the top */
  padding: clamp(10px, 3vw, 15px) 0; /* Responsive padding */
  border-top: 1px solid #eee;
  font-size: 1rem;
  width: 95%; /* Responsive width */
  margin: 20px auto 0 auto;

  @media (max-width: 576px) {
     /* Stack columns on mobile */
    align-items: center;
    gap: 150px;
  }
`;

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(3px, 1vw, 5px); /* Responsive gap */
  width: 48%; /* Distribute space */
  box-sizing: border-box; /* Include padding/border in width */

  @media (max-width: 576px) {
    width: 100%; /* Full width when stacked */
    align-items: center; /* Center text when stacked */
  }
`;

const MainScoreLabel = styled.span`
  font-weight: 700;
  color: #29B185;
  font-size: clamp(1.1rem, 3vw, 1.3rem); /* Responsive font size */
`;

const CriteriaLabel = styled.span`
  font-weight: 600;
  color: #CCCCCC;
  font-size: clamp(0.9rem, 2.5vw, 1.1rem); /* Responsive font size */
`;

const MatchPercentageValue = styled.span`
  font-weight: 700;
  color: #29B185;
  font-size: clamp(1.3rem, 3.5vw, 1.5rem); /* Responsive font size */
`;

const CriteriaScoreValue = styled.span`
  font-weight: 700;
  color: #CCCCCC;
  font-size: clamp(0.9rem, 2.5vw, 1.1rem); /* Responsive font size */
`;



const UserTypeBadge = styled.div`
  background-color: #C9EBE0;
  color: #29B185;
  padding: clamp(5px, 2vw, 10px) clamp(20px, 8vw, 50px); /* Responsive padding */
  border-radius: 20px;
  font-size: clamp(1rem, 3vw, 1.5rem); /* Responsive font size */
  align-self: center;
  margin-top: 15px;
  text-transform: uppercase;
  font-family: 'Yanone Kaffeesatz', sans-serif; /* Keep font family */
  width: fit-content; /* Fit content, but can be responsive with padding */
  margin: 15px auto 0 auto; /* Center horizontally, keep top margin */

  @media (max-width: 576px) {
    padding: clamp(5px, 3vw, 8px) clamp(15px, 6vw, 40px);
    font-size: clamp(1rem, 4vw, 1.5rem);
  }
`;
const ActionButtons = styled.div`
  display: flex;
  justify-content: space-around; /* Distribute space */
  gap: 70%; /* Responsive gap */
  margin-top: 25px;
  width: 95%; /* Responsive width */
  margin: 25px auto 0 auto; /* Center horizontally, keep top margin */

  @media (max-width: 576px) {
     /* Stack buttons on mobile */
    align-items: center;
    gap: 150px; /* Smaller gap when stacked */
  }
`;

const ActionButton = styled.button`
  display: flex;
  flex-direction: column; /* Stack icon above text */
  align-items: center;
  border: none;
  background-color: transparent;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  gap: 5px; /* Space between icon and text */
  padding: 0;
  border-radius: 0;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  ${props => props.$primary && `
    color: #333;
  `}

  ${props => props.$secondary && `
    color: #333;
  `}
`;

const ActionButtonIcon = styled.img`
  width: clamp(30px, 8vw, 40px); /* Responsive icon size */
  height: clamp(30px, 8vw, 40px);
  ${props => props.$color === 'white' && `
    filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
  `}
  ${props => props.$color === 'black' && `
    filter: invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%);
  `}
`;

const ActionButtonText = styled.span`
  font-size: clamp(0.9rem, 2.5vw, 1rem); /* Responsive text size */
  font-weight: 500;
`;
const NoPropertiesMessage = styled.div`
    text-align: center;
    font-size: clamp(1.2rem, 3vw, 1.5rem);
    color: #555;
    padding: clamp(30px, 8vw, 50px);
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    max-width: 600px;
    margin: 50px auto;

    @media (max-width: 576px) {
        padding: 20px;
        margin: 30px auto;
    }
`;

// --- MODAL STYLES (for confirmation modal) ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: clamp(20px, 5vw, 30px);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  text-align: center;
  max-width: 500px;
  width: 90%;

  @media (max-width: 576px) {
    max-width: 90%;
    padding: 15px;
    border-radius: 8px;
  }
`;

const ModalMessage = styled.p`
  font-size: clamp(1rem, 2.8vw, 1.1rem);
  color: #333;
  text-align: justify;
  margin-bottom: clamp(15px, 4vw, 25px);
  line-height: 1.5;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  gap: clamp(10px, 3vw, 15px);

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button`
  padding: clamp(10px, 3vw, 12px) clamp(15px, 5vw, 25px);
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  font-weight: 600;
  transition: background-color 0.3s;

  &.cancel {
    background-color: #ccc;
    color: #333;
  }
  &.cancel:hover {
    background-color: #bbb;
  }

  &.confirm {
    background-color: #29b185;
    color: #fff;
  }
  &.confirm:hover {
    background-color: #28a881;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const TrendingCardsContent = ({ t }) => (
    <NoPropertiesMessage>{t('No_Trending_Properties_Available')}</NoPropertiesMessage>
);

const PendingPropertyView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const slickRef = useRef(null);
    const [isCentered, setIsCentered] = useState(false);
    const containerRef = useRef(null);
    const { Id } = useParams();
    const [demands, setDemands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [mainDisplayImage, setMainDisplayImage] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // Helper function to get the appropriate room/unit label based on property type
    const getRoomLabel = (propertyType) => {
        if (!propertyType) return t("Bedrooms");
        const type = propertyType.toLowerCase();
        if (type === "multifamily") {
            return t("Units");
        }
        if (type === "plot") {
            return t("Index");
        }
        // For apartment, house/villa, and others
        return t("Bedrooms");
    };

    useEffect(() => {
        const fetchDemands = async () => {
            try {
                const response = await authFetch(`${BASE_URL}/property/latest/demands?limit=30`);
                if (!response.ok) {
                    throw new Error('Failed to fetch demands');
                }

                const contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    const data = await response.json();
                    setDemands(data);
                } else {
                    const text = await response.text();
                    throw new Error(`Server returned a non-JSON response. Check your API route. Response was: "${text.substring(0, 50)}..."`);
                }
            } catch (err) {
                setError(err.message);
                console.error("Error fetching demands:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDemands();

        // const intervalId = setInterval(fetchDemands, 60000);

        return () => {
      // Polling disabled to prevent property changes during animation
            // clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 992);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchPropertyDetails = async () => {
            if (!Id) {
                setError("Property ID is missing from the URL.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setProperty(null);
            setError(null);

            try {
                const response = await authFetch(`${BASE_URL}/property/matching/properties`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const allProperties = responseData.data;

                const foundProperty = allProperties.find(
                    (p) => p.Property.id === Id
                );

                if (foundProperty) {
                    setProperty(foundProperty);
                } else {
                    setError("Property not found.");
                }

            } catch (err) {
                setError("Failed to fetch property details. Please try again later.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchPropertyDetails();
    }, [Id]);

    useEffect(() => {
        if (property) {
            setCurrentImageIndex(0);
        }
    }, [property]);

    useEffect(() => {
        const checkOverflow = () => {
            const container = containerRef.current;
            if (container) {
                // Centered is true if content is NOT overflowing
                setIsCentered(container.scrollWidth <= container.clientWidth);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);

        return () => {
      // Polling disabled to prevent property changes during animation
            window.removeEventListener('resize', checkOverflow);
        };
    }, [property]);


    const allImages = property 
  ? [property.Property.PropertyShowcase.mainImage, ...(property.Property.PropertyShowcase.images || [])].filter(Boolean)
  : [];

    const handleImageSlider = (direction) => {
    const totalImages = allImages.length;
    if (totalImages <= 1) return;

    let newIndex;
    if (direction === 'left') {
        newIndex = (currentImageIndex - 1 + totalImages) % totalImages;
    } else {
        newIndex = (currentImageIndex + 1) % totalImages;
    }
    setCurrentImageIndex(newIndex);
};


    const handleAccept = () => {
        if (!property) return;
        setShowConfirmationModal(true);
    };

    const confirmAccept = async () => {
        setShowConfirmationModal(false);

        if (!property) {
            console.error('❌ No property data available');
            return;
        }

        const matchId = property.id;
        const offerPropertyId = property.offerPropertyId || property.Property?.id;

        console.log('📝 Match ID:', matchId);
        console.log('📝 Offer Property ID:', offerPropertyId);

        try {
            const response = await authFetch(
                `${BASE_URL}/property/matching/updateMatchStatus`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: matchId,
                        status: 'accepted',
                        offerPropertyId,
                    }),
                }
            );

            console.log('📡 Response status:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Match accepted successfully:', data);
                // Navigate to MyMatchings page
                navigate('/MyMatchings');
            } else {
                const errorData = await response.json();
                console.error('❌ Failed to accept match:', errorData);
            }
        } catch (error) {
            console.error('❌ Error accepting match:', error);
        }
    };

    const cancelAccept = () => {
        setShowConfirmationModal(false);
    };

    const handleSkip = async () => {
        console.log('🎯 Skip clicked!');
        console.log('📦 Property data:', property);

        if (!property) {
            console.error('❌ No property data available');
            return;
        }

        const matchId = property.id;
        const offerPropertyId = property.offerPropertyId || property.Property?.id;

        console.log('📝 Match ID:', matchId);
        console.log('📝 Offer Property ID:', offerPropertyId);

        try {
            const response = await authFetch(
                `${BASE_URL}/property/matching/updateMatchStatus`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: matchId,
                        status: 'skipped',
                        offerPropertyId,
                    }),
                }
            );

            console.log('📡 Response status:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Match skipped successfully:', data);
                // Navigate to MyMatchings page
                navigate('/MyMatchings');
            } else {
                const errorData = await response.json();
                console.error('❌ Failed to skip match:', errorData);
            }
        } catch (error) {
            console.error('❌ Error skipping match:', error);
        }
    };

    let rightPanelContent;

    if (loading) {
        rightPanelContent = <NoPropertiesMessage>{t('Loading_Property_Data')}</NoPropertiesMessage>;
    } else if (error) {
        rightPanelContent = <NoPropertiesMessage>{error}</NoPropertiesMessage>;
    } else if (!property) {
        rightPanelContent = <NoPropertiesMessage>{t('No_Property_Data_Available')}</NoPropertiesMessage>;
    } else {
        const {
            Property: propertyData,
            matchPercentage,
            mustCriteriaScore,
            shouldCriteriaScore,
            mustCriteriaValueScore,
            shouldCriteriaValueScore
        } = property;

        const address = propertyData?.PropertyAddresses?.[0] || {};
        const showcase = propertyData?.PropertyShowcase || {};
        const user = propertyData?.User || {};
        const allImages = [showcase.mainImage, ...(showcase.images || [])].filter(Boolean);

        rightPanelContent = (
            <PropertyCardWrapper>
                <MainImageContainer>
    <MainImage
        src={allImages[currentImageIndex]}
        alt={t('Main_Property')}
        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/800x400/EEEEEE/333333?text=' + t('Image_Not_Available'); }}
    />

    {/* Navigation Arrows */}
    {allImages.length > 1 && (
        <>
            <ImageNavButton className="left" onClick={() => handleImageSlider('left')}>
                <ImageNavIcon src={ChevronLeftIcon} alt={t('Previous')} />
            </ImageNavButton>
            <ImageNavButton className="right" onClick={() => handleImageSlider('right')}>
                <ImageNavIcon src={ChevronRightIcon} alt={t('Next')} />
            </ImageNavButton>
        </>
    )}

    {/* Dot Indicators */}
    {allImages.length > 1 && (
        <DotIndicatorContainer>
            {allImages.map((_, index) => (
                <DotIndicator 
                    key={index}
                    $active={currentImageIndex === index}
                    onClick={() => setCurrentImageIndex(index)}
                />
            ))}
        </DotIndicatorContainer>
    )}
</MainImageContainer>

                {/* <ThumbnailsCarouselWrapper>

                    <ThumbnailsContainer ref={containerRef} $isCentered={isCentered}>
                        {allImages.length > 1 && (
                            <>
                                <ImageNavButton className="left" onClick={() => handleImageSlider('left')}>
                                    <ImageNavIcon src={ChevronLeftIcon} alt={t('Previous')} />
                                </ImageNavButton>
                                <ImageNavButton className="right" onClick={() => handleImageSlider('right')}>
                                    <ImageNavIcon src={ChevronRightIcon} alt={t('Next')} />
                                </ImageNavButton>
                            </>
                        )}
                        {allImages.map((imgUrl, index) => (
                            <ThumbnailImageWrapper
                                key={index}
                                $active={currentImageIndex === index}
                                onClick={() => {
                                    setCurrentImageIndex(index);
                                }}
                            >
                                <ThumbnailImage
                                    src={imgUrl}
                                    alt={t('Image')}
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/80x80/CCCCCC/666666?text=X'; }}
                                />

                            </ThumbnailImageWrapper>


                        ))}


                    </ThumbnailsContainer>
                </ThumbnailsCarouselWrapper> */}
                <PropertyDetailsGrid>
                    <DetailItem><DetailLabel>{t('zip')}</DetailLabel></DetailItem>
                    <DetailItem><DetailLabel>{t('city')}</DetailLabel></DetailItem>
                    <DetailItem><DetailLabel>{t('Type')}</DetailLabel></DetailItem>
                    <DetailItem><DetailLabel>{t('sqm')}</DetailLabel></DetailItem>
                    <DetailItem><DetailLabel>{getRoomLabel(propertyData?.propertyType)}</DetailLabel></DetailItem>
                    <DetailItem><DetailLabel>{t('Budget')}</DetailLabel></DetailItem>
                </PropertyDetailsGrid>
                <PropertyDetailsGrid2>
                    <DetailItem><DetailValue>{address?.zipCode || 'N/A'}</DetailValue></DetailItem>
                    <DetailItem><DetailValue>{address?.city || 'N/A'}</DetailValue></DetailItem>
                    <DetailItem><DetailValue>{propertyData?.propertyType || 'N/A'}</DetailValue></DetailItem>
                    <DetailItem><DetailValue>{propertyData?.squareMeters || 'N/A'}</DetailValue></DetailItem>
                    <DetailItem><DetailValue>{propertyData?.bedrooms || propertyData?.units ||'N/A'}</DetailValue></DetailItem>
                    <DetailItem>
                        <DetailValue>
                            {propertyData?.price ? formatWithApostrophe(propertyData.price) : 'N/A'} {propertyData?.currency || ''}
                        </DetailValue>
                    </DetailItem>
                </PropertyDetailsGrid2>
                <MatchScoreSection>
                    <ScoreColumn>
                        <MainScoreLabel>{t('Matching')}</MainScoreLabel>
                        <CriteriaLabel>{t('Must_Criteria')}</CriteriaLabel>
                        <CriteriaLabel>{t('Nice_To_Have')}</CriteriaLabel>
                    </ScoreColumn>
                    <ScoreColumn style={{ textAlign: 'right' }}>
                        <MatchPercentageValue>
                            {matchPercentage ? `${parseFloat(matchPercentage).toFixed(2)}%` : 'N/A'}
                        </MatchPercentageValue>
                        <CriteriaScoreValue>
                            {mustCriteriaValueScore || mustCriteriaScore || 'N/A'}
                        </CriteriaScoreValue>
                        <CriteriaScoreValue>
                            {shouldCriteriaValueScore || shouldCriteriaScore || 'N/A'}
                        </CriteriaScoreValue>
                    </ScoreColumn>
                </MatchScoreSection>
                <FooterActionsContainer>
                    <ActionButton $secondary onClick={handleSkip}>
                        <ActionButtonIcon src={TrashXmarkIcon} alt={t('Skip')} />
                        <ActionButtonText>{t('Skip')}</ActionButtonText>
                    </ActionButton>

                    <UserTypeBadge>
                        {user.isPrivate ? t('PRIVATE_INDIVIDUAL') : t('COMPANY_AGENCY')}
                    </UserTypeBadge>
                    
                    <ActionButton $primary onClick={handleAccept}>
                        <ActionButtonIcon src={thumbsup} alt={t('Discover_More')} />
                        <ActionButtonText>{t('Discover_More')}</ActionButtonText>
                    </ActionButton>
                </FooterActionsContainer>
            </PropertyCardWrapper>
        );
    }

    return (
        <DemandDashboardContainer>
          <div className={styles.leftPanel}>
            {!isLoading && !error && demands.length > 0 && (
              <div className={styles.newSearchesHeader}>
                <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
              </div>
            )}
            {demands.length > 0 && (
              <div className={styles.propertyList}>
                <NewOffersSwiper items={demands} className={styles.mySwiper} />
              </div>
            )}
          </div>
            <RightPanel>
                {rightPanelContent}
            </RightPanel>

            {showConfirmationModal && (
                <ModalOverlay>
                    <ModalContent>
                        <ModalMessage>
                            {t("Do you want to view the details? Once confirmed, your information will be shared with the property owner.")}
                        </ModalMessage>
                        <ModalButtonContainer>
                            <ModalButton className="cancel" onClick={cancelAccept}>
                                {t("Cancel")}
                            </ModalButton>
                            <ModalButton className="confirm" onClick={confirmAccept}>
                                {t("Confirm")}
                            </ModalButton>
                        </ModalButtonContainer>
                    </ModalContent>
                </ModalOverlay>
            )}
        </DemandDashboardContainer>
    );
};

export default PendingPropertyView;
